import React, { useRef, useState } from "react";
import { FiAlertTriangle, FiCheckCircle, FiChevronDown, FiPlay, FiXCircle } from "react-icons/fi";
import Button from "components/atoms/Button";
import InputField from "components/atoms/InputField";
import InputSelect from "components/atoms/InputSelect";
import LoadingSpinner from "components/LoadingSpinner";
import standardTreatmentService, { errorMessages } from "services/StandardTreatment";
import { ErrorState } from "pages/StandardTreatment/shared/ui";
import {
  INITIAL_MEDICATION,
  INITIAL_PATIENT,
  MEDICATION_KEYS,
  SIMULATION_FACT_FIELDS,
  VERDICT_LABELS,
  fieldByValue,
  pickValue,
  toOption,
} from "../constants";

const AGE_UNITS = [
  { label: "Tahun", value: "Tahun" },
  { label: "Bulan", value: "Bulan" },
];

const verdictTone = (verdict) =>
  ["MATCH", "MATCHED"].includes(verdict) ? "success" : ["SKIP", "SKIPPED"].includes(verdict) ? "neutral" : "error";

// Pemetaan form → SimulateRequest (HANDOFF §5.1): usia ke bulan, obat > 0 = diberikan.
const toRequest = (versionId, patient, medication) => ({
  versionId,
  patientFacts: {
    disease_degree: patient.disease_degree,
    recurrence: patient.recurrence,
    g6pd_result: patient.g6pd_result,
    parasite: patient.parasite,
    age_months: patient.age_unit === "Tahun" ? Number(patient.age) * 12 : Number(patient.age),
    weight_kg: Number(patient.weight_kg),
    pregnancy: patient.pregnancy,
    breastfeeding: patient.breastfeeding,
    lab_checking_type: patient.lab_checking_type,
  },
  medicationGiven: Object.fromEntries(
    MEDICATION_KEYS.map((k) => [k, Number(medication[k]) > 0 ? Number(medication[k]) : null])
  ),
});

// gagal_pada punya beberapa bentuk; tampilkan sebagai pasangan kunci-nilai.
const Detail = ({ value }) =>
  Object.entries(value || {})
    .filter(([, v]) => v !== undefined && v !== null)
    .map(([k, v]) => (
    <span key={k} className="mps-kv">
      <code className="mps-code">{k}</code> {Array.isArray(v) ? v.join(", ") : String(v)}
    </span>
  ));

const SimulationStep = ({ versionId, drugs }) => {
  const [patient, setPatient] = useState(INITIAL_PATIENT);
  const [medication, setMedication] = useState(INITIAL_MEDICATION);
  const [state, setState] = useState({ status: "idle", result: null, error: null });
  const [openTrace, setOpenTrace] = useState(false);
  const traceRef = useRef(null);
  const result = state.result;

  const nameOf = (key) => drugs.find((d) => d.medication_key === key)?.name || key;
  const setFact = (key, value) => setPatient((prev) => ({ ...prev, [key]: value }));
  const ageMonths = patient.age === "" ? "—" : toRequest(versionId, patient, {}).patientFacts.age_months;
  const incomplete = !patient.parasite.length || patient.age === "" || patient.weight_kg === "";

  const run = () => {
    setState({ status: "running", result: null, error: null });
    setOpenTrace(false);
    standardTreatmentService
      .simulate(toRequest(versionId, patient, medication))
      .then((data) => setState({ status: "done", result: data, error: null }))
      .catch((err) => setState({ status: "error", result: null, error: errorMessages(err) }));
  };

  const showTrace = () => {
    setOpenTrace(true);
    traceRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const renderFact = (key) => {
    const field = fieldByValue(key);
    if (field.type === "species")
      return (
        <InputSelect
          label={`${field.label} (satu = tunggal, > 1 = mix)`}
          isMultipleSelect
          data={field.choices}
          value={field.choices.filter((c) => patient[key].includes(c.value))}
          onChange={(selected) => setFact(key, (selected || []).map((s) => s.value))}
        />
      );
    return (
      <InputSelect
        label={field.label}
        isClearable={false}
        data={field.choices}
        value={toOption(field.choices, patient[key])}
        onChange={(sel) => setFact(key, pickValue(sel))}
      />
    );
  };

  const drugChecks = result?.checks.filter((c) => c.medication_key) || [];
  const ruleChecks = result?.checks.filter((c) => c.check) || [];
  const warnings = ruleChecks.filter((c) => c.verdict !== "MATCH");

  return (
    <div className="row">
      <div className="col-lg-6 mps-field">
        <section className="mps-subcard h-100">
          <h3 className="mps-section-title mt-0">Simulasi pasien</h3>
          <p className="mps-muted">Data pasien fiktif untuk menguji versi ini (termasuk Draft). Tidak disimpan.</p>
          <div className="row">
            {SIMULATION_FACT_FIELDS.map((key) => (
              <div key={key} className={`${key === "parasite" ? "col-12" : "col-sm-6"} mps-field`}>
                {renderFact(key)}
              </div>
            ))}
            <div className="col-sm-6 mps-field">
              <div className="mps-age">
                <InputField type="number" label="Usia" value={patient.age} onChange={(e) => setFact("age", e.target.value)} />
                <InputSelect
                  label="Satuan"
                  isClearable={false}
                  data={AGE_UNITS}
                  value={toOption(AGE_UNITS, patient.age_unit)}
                  onChange={(sel) => setFact("age_unit", pickValue(sel))}
                />
              </div>
              <small className="mps-helper">= {ageMonths} bulan (age_months)</small>
              {/* HANDOFF §5.1: "0 Tahun" dan "Bulan" ≥ 96 dinilai berbeda oleh logika lama. */}
              {((patient.age_unit === "Tahun" && Number(patient.age) < 1 && patient.age !== "") ||
                (patient.age_unit === "Bulan" && Number(patient.age) >= 12)) && (
                <small className="mps-helper is-error">
                  Isi usia di bawah 1 tahun dalam Bulan, selebihnya dalam Tahun.
                </small>
              )}
            </div>
            <div className="col-sm-6 mps-field">
              <InputField type="decimal" label="Berat badan (kg)" value={patient.weight_kg} onChange={(e) => setFact("weight_kg", e.target.value)} />
              <small className="mps-helper">Desimal dipotong oleh backend (39,9 → 39).</small>
            </div>
          </div>

          <h4 className="mps-subsection-title mt-0">Obat yang diberikan (medicationGiven)</h4>
          <p className="mps-helper mb-3">Jumlah per medication key; kosong / 0 = tidak diberikan.</p>
          <div className="row">
            {MEDICATION_KEYS.map((key) => (
              <div key={key} className="col-6 col-md-4 col-lg-6 col-xl-4 mps-field">
                <InputField
                  type="decimal"
                  label={nameOf(key)}
                  placeholder="0"
                  value={medication[key] || ""}
                  onChange={(e) => setMedication((prev) => ({ ...prev, [key]: e.target.value }))}
                />
              </div>
            ))}
          </div>

          <span className="mps-block-btn">
            <Button type="button" variant="primary" size="normal" onClick={run} isDisabled={incomplete || state.status === "running"}>
              <FiPlay size={16} className="mr-2" />
              {state.status === "running" ? "Menjalankan..." : "Jalankan simulasi"}
            </Button>
          </span>
          {incomplete && <small className="mps-helper is-error">Semua data pasien wajib diisi (parasit, usia, berat).</small>}
        </section>
      </div>

      <div className="col-lg-6 mps-field">
        <section className="mps-subcard h-100" aria-live="polite">
          <h3 className="mps-section-title mt-0">Hasil rekomendasi</h3>
          {state.status === "idle" && (
            <div className="mps-empty">
              Isi data pasien lalu klik <strong>Jalankan simulasi</strong> untuk mengevaluasi versi ini.
            </div>
          )}
          {state.status === "running" && <LoadingSpinner />}
          {state.status === "error" && <ErrorState title="Simulasi gagal" messages={state.error} onRetry={run} />}
          {result && (
            <>
              <div className={`mps-alert ${result.status === "Standard" ? "is-success" : "is-error"}`}>
                {result.status === "Standard" ? <FiCheckCircle size={20} className="mps-alert-icon" /> : <FiXCircle size={20} className="mps-alert-icon" />}
                <div>
                  <p className="mps-alert-title">{result.status}</p>
                  <p className="mps-alert-text">{result.reason || "Obat dan dosis yang diberikan sesuai rule yang cocok."}</p>
                </div>
              </div>

              <dl className="mps-summary mb-3">
                <dt>Rule cocok</dt>
                <dd>
                  {result.matchedRule
                    ? `${result.matchedRule.source_rule_code} #${result.matchedRule.source_rule_seq} — ${result.matchedRule.name}`
                    : "Tidak ada"}
                </dd>
                <dt>Dievaluasi pada</dt>
                <dd>
                  Versi {result.version.version} ({result.version.status})
                  {result.version.release_no ? ` · snapshot rilis #${result.version.release_no}` : " · belum pernah dirilis"}
                </dd>
              </dl>

              <h4 className="mps-subsection-title">{result.status === "Standard" ? "Rekomendasi terapi" : "Terapi yang gagal"}</h4>
              {drugChecks.length ? (
                <ul className="mps-list">
                  {drugChecks.map((c) => (
                    <li key={c.medication_key} className="mps-recommendation">
                      <div className="flex-grow-1">
                        <p className="mps-therapy-name">{nameOf(c.medication_key)}</p>
                        <p className="mps-muted mb-0">
                          {c.operator === "DOSE_BAND"
                            ? `${c.band}: ${c.expected} ${c.dose_unit} · diberikan ${c.given ?? "—"}`
                            : `PRESENT${c.exclusive_group ? ` · grup ${c.exclusive_group}` : ""} · diberikan ${c.given ?? "—"}`}
                        </p>
                      </div>
                      <span className={`mps-pill is-${verdictTone(c.verdict)}`}>{VERDICT_LABELS[c.verdict] || c.verdict}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mps-muted">
                  {result.status === "Standard" ? "Tidak ada pemeriksaan per obat." : "Tidak ada rule yang kondisinya cocok, jadi tidak ada pemeriksaan obat."}
                </p>
              )}

              {/* reason sudah tampil di alert status; di sini hanya check whole-rule yang gagal. */}
              <h4 className="mps-subsection-title">Peringatan</h4>
              {warnings.length ? (
                <>
                  {warnings.map((c) => (
                    <div key={c.check + (c.exclusive_group || "")} className="mps-alert is-warning">
                      <FiAlertTriangle size={18} className="mps-alert-icon" />
                      <p className="mps-alert-text">
                        {c.check === "EXCLUSIVE_GROUP"
                          ? `Grup ${c.exclusive_group}: ${c.terisi} dari ${c.anggota.join(", ")} diberikan (${VERDICT_LABELS[c.verdict] || c.verdict})`
                          : `Obat di luar rule: ${(c.obat_tak_diharapkan || []).join(", ") || "—"}`}
                      </p>
                    </div>
                  ))}
                </>
              ) : (
                <p className="mps-muted">Tidak ada peringatan.</p>
              )}

              <button type="button" className="mps-link mb-3" onClick={showTrace}>
                Lihat evaluasi aturan
              </button>

              <details className="mps-trace" ref={traceRef} open={openTrace} onToggle={(e) => setOpenTrace(e.currentTarget.open)}>
                <summary>
                  <FiChevronDown size={16} className="mps-trace-chevron" />
                  <span>Jejak rule & checks (debug)</span>
                  <span className="mps-pill is-neutral">{result.trace.length} rule</span>
                </summary>
                <ul>
                  {result.trace.map((t) => (
                    <li key={`${t.source_rule_code}-${t.source_rule_seq}`} className="mps-trace-entry">
                      <span>
                        <strong>
                          {t.source_rule_code} #{t.source_rule_seq}
                        </strong>{" "}
                        {t.name}
                        <span className="d-block">
                          {t.verdict === "MATCHED" ? `${t.kondisi_lolos} kondisi lolos` : <Detail value={t.gagal_pada} />}
                        </span>
                      </span>
                      <span className={`mps-pill is-${verdictTone(t.verdict)}`}>{VERDICT_LABELS[t.verdict]}</span>
                    </li>
                  ))}
                  {ruleChecks.map((c) => (
                    <li key={`check-${c.check}-${c.exclusive_group || ""}`} className="mps-trace-entry">
                      <span>
                        <code className="mps-code">{c.check}</code>{" "}
                        <Detail value={{ exclusive_group: c.exclusive_group, anggota: c.anggota, terisi: c.terisi, obat_tak_diharapkan: c.obat_tak_diharapkan }} />
                      </span>
                      <span className={`mps-pill is-${verdictTone(c.verdict)}`}>{VERDICT_LABELS[c.verdict] || c.verdict}</span>
                    </li>
                  ))}
                </ul>
              </details>
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default SimulationStep;
